/* ------------------------------------------------------------------
  File: Accordion.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable accordion component for the Accend Holdings website.
//   - Supports single-open and multiple-open behavior.
//   - Supports default open items, variants, CMS-ready item data,
//     keyboard-accessible buttons, and dark-surface compatibility.
//   - Uses the acc-accordion namespace to avoid conflicts with Bootstrap.
//   - Suitable for FAQs, service details, investor information,
//     governance notes, and content disclosure sections.

/**
 * Accordion
 *
 * A reusable accordion component for expandable content sections.
 *
 * @param {Array} items
 * Array of accordion items.
 *
 * Each item should support:
 * - id: string
 * - title: React.ReactNode
 * - content: React.ReactNode
 *
 * @param {boolean} [allowMultiple=false]
 * Allows multiple accordion items to stay open at the same time.
 *
 * @param {string[]} [defaultOpenItems=[]]
 * IDs of accordion items that should be open by default.
 *
 * @param {"default" | "bordered" | "minimal" | "inverse"} [variant="default"]
 * Controls the accordion visual style.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import { useState } from "react";
import { LuChevronDown } from "react-icons/lu";
import "./Accordion.css";

const Accordion = ({
  items = [],
  allowMultiple = false,
  defaultOpenItems = [],
  variant = "default",
  className = "",
  ...props
}) => {
  const [openItems, setOpenItems] = useState(defaultOpenItems);

  const accordionClasses = [
    "acc-accordion",
    `acc-accordion--${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const handleToggle = (itemId) => {
    setOpenItems((currentOpenItems) => {
      const isOpen = currentOpenItems.includes(itemId);

      if (allowMultiple) {
        return isOpen
          ? currentOpenItems.filter((id) => id !== itemId)
          : [...currentOpenItems, itemId];
      }

      return isOpen ? [] : [itemId];
    });
  };

  if (!items.length) {
    return null;
  }

  return (
    <div className={accordionClasses} {...props}>
      {items.map((item) => {
        const isOpen = openItems.includes(item.id);
        const panelId = `accordion-panel-${item.id}`;
        const buttonId = `accordion-button-${item.id}`;

        return (
          <div
            key={item.id}
            className={[
              "acc-accordion__item",
              isOpen ? "acc-accordion__item--open" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <h3 className="acc-accordion__heading">
              <button
                id={buttonId}
                type="button"
                className="acc-accordion__trigger"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => handleToggle(item.id)}
              >
                <span className="acc-accordion__title">{item.title}</span>

                <span className="acc-accordion__icon" aria-hidden="true">
                  <LuChevronDown />
                </span>
              </button>
            </h3>

            <div
              id={panelId}
              className="acc-accordion__panel"
              role="region"
              aria-labelledby={buttonId}
              hidden={!isOpen}
            >
              <div className="acc-accordion__content">{item.content}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Accordion;